import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Loom from "./Loom";
import PullTassel from "./PullTassel";
import OutputCard from "./OutputCard";
import YarnsStrip from "./YarnsStrip";
import Paywall from "./Paywall";
import { pullThread } from "../lib/api";
import {
  useDailyPullState,
  useSavedThreads,
  useGuestPullCount,
} from "../lib/hooks";
import { useMemberAccess } from "../lib/memberstack";
import { toast } from "sonner";

/**
 * Hero — composes the loom + pull tassel + output card region.
 *
 * Member tiers:
 *   - "member"  → unlimited pulls; daily-rhythm copy is honoured (the soft
 *                  "today's loom is woven" framing after three pulls in one day).
 *   - "guest"   → lifetime 3-pull device limit; paywall replaces the tassel
 *                  after the third pull. Past pulls remain re-readable.
 *
 * Auto-pull (?monday=1) is preserved for both tiers. For guests it counts
 * as one of their three free pulls.
 */
export default function Hero() {
  const [card, setCard] = useState(null);
  const [isPulling, setIsPulling] = useState(false);

  const access = useMemberAccess();
  // Anyone with an authenticated Memberstack session gets unlimited pulls,
  // even if our price-ID match fails (defensive — covers the case where
  // Memberstack regenerates a price ID and we haven't redeployed). If you
  // want to tighten this later, change the predicate to `=== "member"`.
  const isMember =
    access.status === "member" || access.status === "unauthorised";
  const isLoadingAccess = access.status === "loading";

  const { state: daily, setPull } = useDailyPullState();
  const { count: guestCount, increment: incrementGuest, limit: guestLimit } =
    useGuestPullCount();
  const { isSaved, toggle } = useSavedThreads();

  const autoTriggered = useRef(false);

  // For guests, the pull count shown in the UI is their lifetime guest count.
  // For members, we keep the per-day count (today's ritual framing).
  const effectivePullCount = isMember ? daily.count : guestCount;
  const guestCapped = !isMember && guestCount >= guestLimit;

  const handlePull = useCallback(async () => {
    if (isPulling) return;
    if (isLoadingAccess) return; // wait for auth check before pulling
    if (guestCapped) {
      // Don't pull — paywall is already on screen instead of the tassel.
      return;
    }
    setIsPulling(true);
    try {
      const res = await pullThread({
        pullCount: effectivePullCount,
        sessionId: isMember ? daily.sessionId : null,
        excludeYarn: card?.yarn || null,
      });
      await new Promise((r) => setTimeout(r, 550));
      setCard(res.card);
      if (isMember) {
        setPull(
          res.pull_count > daily.count ? res.pull_count : daily.count + 1,
          res.session_id
        );
      } else {
        incrementGuest();
      }
    } catch (err) {
      toast.error("The thread snagged. Please try again.");
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setIsPulling(false);
    }
  }, [
    isPulling,
    isLoadingAccess,
    guestCapped,
    effectivePullCount,
    isMember,
    daily.sessionId,
    daily.count,
    card,
    setPull,
    incrementGuest,
  ]);

  // Auto-pull on arrival when the URL signals an external trigger.
  // Skipped if the visitor is already at the guest cap — they get the
  // paywall on arrival instead, which converts better than a denied pull.
  useEffect(() => {
    if (autoTriggered.current) return;
    if (typeof window === "undefined") return;
    if (isLoadingAccess) return;
    if (guestCapped) {
      autoTriggered.current = true;
      return;
    }
    const params = new URLSearchParams(window.location.search);
    const wants =
      params.get("monday") || params.get("auto") || params.get("pull");
    if (wants && wants !== "0") {
      autoTriggered.current = true;
      const t = setTimeout(() => handlePull(), 1800);
      return () => clearTimeout(t);
    }
  }, [isLoadingAccess, guestCapped, handlePull]);

  // Eyebrow copy adapts to member status (without ever feeling transactional)
  const eyebrow = isMember
    ? access.member?.firstName
      ? `Welcome back, ${access.member.firstName} · Your daily thread`
      : "Welcome back · Your daily thread"
    : "A small daily ritual";

  const introBody = isMember ? (
    <>
      Four threads sit on the loom — Centre, Sharpen, Reflect, Act. Pull one
      when the morning needs a nudge, the afternoon needs a quote, or the
      week needs a "what if". Your small daily ritual inside the 1% Club.
    </>
  ) : (
    <>
      Four threads sit on the loom — Centre, Sharpen, Reflect, Act. Pull one
      when the morning needs a nudge, the afternoon needs a quote, or the
      week needs a "what if". Three pulls are on the house — the rest live
      inside The Weave's 1% Club.
    </>
  );

  return (
    <section
      className="relative pt-4 md:pt-10 pb-16 md:pb-24"
      data-testid="hero-section"
      data-member-status={access.status}
    >
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          className="text-center max-w-3xl mx-auto"
        >
          <p
            className="text-xs uppercase tracking-[0.22em] text-weave-teal font-medium mb-5"
            data-testid="hero-eyebrow"
          >
            {eyebrow}
          </p>
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#1a1a1a] leading-[1.05]"
            style={{ fontFamily: "Rubik, sans-serif" }}
          >
            Pull a thread.
            <span className="block">See what surfaces.</span>
          </h1>
          <p className="mt-6 text-base md:text-lg text-[#3a3a3a]/85 leading-relaxed max-w-xl mx-auto">
            {introBody}
          </p>
        </motion.div>

        <YarnsStrip />

        {/* Loom — always rendered */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="relative mt-10 md:mt-14 mx-auto max-w-3xl rounded-3xl border border-black/5 bg-white/45 backdrop-blur-[2px] p-3 md:p-6 shadow-[0_22px_60px_rgba(60,40,10,0.08)]"
          data-testid="loom-stage"
        >
          <Loom isPulling={isPulling} lastYarn={card?.yarn} />
        </motion.div>

        {/* Either the pull tassel (when pulls are still available) OR the paywall */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.35 }}
          className="mt-8 md:mt-12"
        >
          {guestCapped ? (
            <Paywall accessStatus={access.status} onRefresh={access.refresh} />
          ) : (
            <PullTassel
              onPull={handlePull}
              isPulling={isPulling}
              pullCount={effectivePullCount}
              lastYarn={card?.yarn}
            />
          )}
        </motion.div>

        {/* Output card */}
        <div
          className="mt-8 md:mt-12 min-h-[60px]"
          aria-live="polite"
          data-testid="output-card-region"
        >
          <AnimatePresence mode="wait">
            {card && !isPulling && (
              <motion.div key="card-wrap">
                <OutputCard
                  card={card}
                  dailyPullCount={effectivePullCount}
                  guestCapped={guestCapped}
                  onPullAgain={handlePull}
                  onToggleSave={(c) => {
                    const wasSaved = isSaved(c.id);
                    toggle(c);
                    if (!wasSaved) {
                      toast.success("Pinned to your loomboard.");
                    }
                  }}
                  isSaved={card ? isSaved(card.id) : false}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
